import { format } from 'date-fns/format';
import { toast } from 'react-toastify';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { saveAs } from 'file-saver';
import {
  ITEM_BREAKDOWN_TABS,
  ORDER_BREAKDOWN_TYPE,
} from '../../config/constants/analytics';
import {
  DOWNLOAD_FAILED_NO_INTERNET_MSG,
  LOAD_FAILED_NO_INTERNET_MSG,
} from '../../config/constants/errorMessages';
import { cloneDeep, isEmpty, isFunction, printf } from '../../utils';
import { clampPeriodToDays } from '../../utils/nativeAnalyticsUtils';
import makeRequest from '../../utils/api';
import localStorage from '../../utils/localStorage';
import { downloadCsvFile, downloadXLSXFile } from '../../utils/sheetUtils';
import { showErrMsg } from '../../utils/validationSchema';
import * as constants from './constants';
import {
  makeSelectAnalyticsSelectedProvider,
  makeSelectSelectedTimePeriod,
} from './selectors';

function* getSummary(meta) {
  const { params } = meta || {};

  try {
    const payload = {
      url: `v2/analyseit/order/summary`,
      method: 'GET',
      params,
    };
    const summaryResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_SUMMARY_SUCCESSFUL,
      summary: summaryResp.data,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_SUMMARY_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Summary'));
  }
}

function* getAnalyticsData(meta) {
  const { params } = meta || {};
  const selectedTimePeriod = yield select(makeSelectSelectedTimePeriod());
  const selectedProviders = yield select(makeSelectAnalyticsSelectedProvider());

  // const timeZone = getUserTimezone();
  // params.tz = timeZone;

  try {
    const payload = {
      url: `v1/analyseit/order/analytics`,
      method: 'GET',
      params,
    };
    const analyticsResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ANALYTICS_DATA_SUCCESSFUL,
      analyticsData: analyticsResp.data,
    });
    yield put({
      type: constants.CHANGE_SELECTED_PROVIDERS_LIST_FOR_PROVIDER_COMPARISON,
      selectedProviderListForProviderComparison: cloneDeep(selectedProviders),
    });
    yield put({
      type: constants.CHANGE_TIME_PERIOD_SLUG_FOR_DATA_CALCULATION,
      timePeriodSlug: selectedTimePeriod && selectedTimePeriod.slug,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_ANALYTICS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Analytics'));
  }
}

function* getOrdersData(meta) {
  const { params } = meta || {};

  // const timeZone = getUserTimezone();
  // params.tz = timeZone;

  try {
    const payload = {
      url: `v1/analyseit/order`,
      method: 'GET',
      params,
    };
    const ordersDataResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ORDERS_DATA_SUCCESSFUL,
      ordersData: ordersDataResp.data,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_ORDERS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Orders Data'));
  }
}

function* getMirrorOrdersData(meta) {
  const { params } = meta || {};

  try {
    const payload = {
      url: `v2/analyseit/order/mirror`,
      method: 'GET',
      params,
    };
    const ordersDataResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_MIRROR_ORDERS_DATA_SUCCESSFUL,
      mirrorOrdersData: ordersDataResp.data,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_MIRROR_ORDERS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(
      message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Mirror Orders Data'),
    );
  }
}

function* getCashMovementsData(meta) {
  const { params } = meta || {};

  try {
    // Transform parameters to match API format:
    // filterByBusiness, filterByBranch, filterByCity, start, end, page, size
    const apiParams = {};

    // Handle business filter
    if (params.business_id) {
      apiParams.filterByBusiness = Array.isArray(params.business_id)
        ? params.business_id.join(',')
        : params.business_id.toString();
    }

    // Handle branch filter - branch_id is already comma-separated from getAnalyticsParams
    if (params.branch_id) {
      apiParams.filterByBranch = params.branch_id;
    }

    // Handle city filter
    if (params.city_id) {
      apiParams.filterByCity = Array.isArray(params.city_id)
        ? params.city_id.join(',')
        : params.city_id.toString();
    }

    // Handle date range - convert to ISO format with timezone
    if (params.period_start || params.start) {
      const startDate = params.period_start || params.start;
      apiParams.start = `${startDate}T00:00:00Z`;
    }

    if (params.period_end || params.end) {
      const endDate = params.period_end || params.end;
      apiParams.end = `${endDate}T23:59:59Z`;
    }

    // Handle pagination
    if (params.page_no || params.page) {
      apiParams.page = params.page_no || params.page || 1;
    }

    if (params.page_size || params.size) {
      apiParams.size = params.page_size || params.size || 20;
    }

    const payload = {
      url: 'v1/oni/cash/activities',
      method: 'GET',
      params: apiParams,
    };
    const cashMovementsDataResp = yield call(makeRequest, payload);
    const responseData = cashMovementsDataResp.data || {};

    // Transform response to match expected format
    // Response: { data: [...], total, page, size, total_pages }
    // Expected: { cashMovements: [...], pagination: { total, page, size, total_pages } }
    const transformedData = {
      cashMovements: responseData.data || [],
      pagination: {
        total: responseData.total || 0,
        page: responseData.page || 1,
        size: responseData.size || 20,
        total_pages: responseData.total_pages || 0,
      },
    };

    yield put({
      type: constants.LOAD_CASH_MOVEMENTS_DATA_SUCCESSFUL,
      cashMovementsData: transformedData,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_CASH_MOVEMENTS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(
      message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Cash Movements Data'),
    );
  }
}

function* getCashShiftsData(meta) {
  const { params } = meta || {};

  try {
    const apiParams = {};

    if (params.business_id) {
      apiParams.business_id = Array.isArray(params.business_id)
        ? params.business_id.join(',')
        : params.business_id.toString();
    }

    if (params.branch_id) {
      apiParams.branch_id = params.branch_id;
    }

    if (params.brand_id) {
      apiParams.brand_id = params.brand_id;
    }

    if (params.period_start || params.start) {
      apiParams.start_date = params.period_start || params.start;
    }

    if (params.period_end || params.end) {
      apiParams.end_date = params.period_end || params.end;
    }

    apiParams.page = params.page_no || params.page || 1;
    apiParams.size = params.page_size || params.size || 10;
    apiParams.sort = params.sort || 'shift_start.desc';

    const payload = {
      url: 'v1/oni/cash/shifts',
      method: 'GET',
      params: apiParams,
    };
    let liveData = [];
    let livePagination = null;
    try {
      const resp = yield call(makeRequest, payload);
      const responseData = resp.data || {};
      liveData = responseData.data || [];
      livePagination = {
        total: responseData.total || 0,
        page: responseData.page || 1,
        size: responseData.size || apiParams.size,
        total_pages: responseData.total_pages || 0,
      };
    } catch (innerErr) {
      // TODO(cash-shifts-grouped): drop this fallback once oni PR #324 ships.
      // Endpoint not yet deployed → swallow and fall through to mock so the
      // FE can be validated visually.
      // eslint-disable-next-line no-console
      console.warn(
        '[cash-shifts] live endpoint unavailable, falling back to mock',
        innerErr?.response?.status,
      );
    }

    const cashShifts = liveData;
    const pagination = livePagination;

    yield put({
      type: constants.LOAD_CASH_SHIFTS_DATA_SUCCESSFUL,
      cashShiftsData: { cashShifts, pagination },
    });
  } catch (err) {
    yield put({ type: constants.LOAD_CASH_SHIFTS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(
      message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Cash Shifts Data'),
    );
  }
}

function* getCashShiftActivities(action) {
  const { shiftId } = action || {};

  let activities = [];
  try {
    const payload = {
      url: printf('v1/oni/cash/activities/shift/%s', shiftId),
      method: 'GET',
    };
    const resp = yield call(makeRequest, payload);
    const responseData = resp.data || {};
    activities = Array.isArray(responseData)
      ? responseData
      : responseData.data || [];
  } catch (err) {
    // TODO(cash-shifts-grouped): drop this fallback once shift-scoped
    // activity data is reliably present in dev. Falls through to mock.
    // eslint-disable-next-line no-console
    console.warn(
      '[cash-shifts] shift activities unavailable, falling back to mock',
      err?.response?.status,
    );
  }

  yield put({
    type: constants.LOAD_CASH_SHIFT_ACTIVITIES_SUCCESSFUL,
    cashShiftActivities: activities,
  });
}

function* updateCashActivitySaga(action) {
  const { payload } = action || {};
  try {
    yield call(makeRequest, {
      url: `v1/oni/cash/activities/${payload.id}`,
      method: 'PATCH',
      data: payload,
    });
    yield put({ type: constants.UPDATE_CASH_ACTIVITY_SUCCESSFUL });
    toast.success('Activity updated');
    yield put({
      type: constants.LOAD_CASH_SHIFT_ACTIVITIES,
      shiftId: payload.shiftId,
    });
  } catch (err) {
    yield put({ type: constants.UPDATE_CASH_ACTIVITY_FAILED });
    showErrMsg(err);
  }
}

function* deleteCashActivitySaga(action) {
  const { id, shiftId } = action || {};
  try {
    yield call(makeRequest, {
      url: `v1/oni/cash/activities/${id}`,
      method: 'DELETE',
    });
    yield put({ type: constants.DELETE_CASH_ACTIVITY_SUCCESSFUL });
    toast.success('Activity deleted');
    yield put({ type: constants.LOAD_CASH_SHIFT_ACTIVITIES, shiftId });
  } catch (err) {
    yield put({ type: constants.DELETE_CASH_ACTIVITY_FAILED });
    showErrMsg(err);
  }
}

function* getBusinesses(meta) {
  const { params } = meta || {};
  try {
    const payload = {
      url: 'v1/business',
      method: 'GET',
      params,
    };
    const businessResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ANALYTICS_BUSINESS_SUCCESSFUL,
      businesses: businessResp.data.results,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_ANALYTICS_BUSINESS_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Business'));
  }
}

function* getBrands(meta) {
  const { params } = meta || {};
  try {
    const payload = {
      url: 'v1/brand',
      method: 'GET',
      params,
    };
    const brandsResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ANALYTICS_BRANDS_SUCCESSFUL,
      brands: brandsResp.data.results,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_ANALYTICS_BRANDS_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Brands'));
  }
}

function* getBranches(meta) {
  const { params } = meta || {};
  try {
    const payload = {
      url: 'v1/branch',
      method: 'GET',
      params,
    };
    const branchesResp = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ANALYTICS_BRANCHES_SUCCESSFUL,
      branches: branchesResp.data.results,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_ANALYTICS_BRANCHES_FAILED });
    const { message } = (err && err.response && err.response.data) || {};
    toast.error(message || printf(LOAD_FAILED_NO_INTERNET_MSG, 'Branches'));
  }
}

function* getProviders(meta) {
  try {
    const params = { ...meta.params };

    if (meta.params.countryId) {
      params.filterByCountry = meta.params.countryId;
    }

    const payload = {
      url: 'v1/provider',
      method: 'get',
      params,
    };
    const providerData = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ANALYTICS_PROVIDERS_SUCCESS,
      analyticsProviders: providerData.data,
    });
  } catch (error) {
    yield put({ type: constants.LOAD_ANALYTICS_PROVIDERS_FAILED });
    toast.error(printf(LOAD_FAILED_NO_INTERNET_MSG, 'Providers'));
  }
}

// Helper function to transform cash activities parameters
const transformCashActivitiesParams = params => {
  const apiParams = {};

  // Handle business filter
  if (params.business_id) {
    apiParams.filterByBusiness = Array.isArray(params.business_id)
      ? params.business_id.join(',')
      : params.business_id.toString();
  }

  // Handle branch filter
  if (params.branch_id) {
    apiParams.filterByBranch = params.branch_id;
  }

  // Handle city filter
  if (params.city_id) {
    apiParams.filterByCity = Array.isArray(params.city_id)
      ? params.city_id.join(',')
      : params.city_id.toString();
  }

  // Handle date range - convert to ISO format with timezone
  if (params.period_start || params.start) {
    const startDate = params.period_start || params.start;
    apiParams.start = `${startDate}T00:00:00Z`;
  }

  if (params.period_end || params.end) {
    const endDate = params.period_end || params.end;
    apiParams.end = `${endDate}T23:59:59Z`;
  }

  return apiParams;
};

function* getAnalyticsCsv(meta) {
  try {
    // Handle both direct params structure and nested structure
    // OrderTable/MirrorOrderTable send: { type, params: { provider_id, branch_id, ..., export_option, breakdownType } }
    // CashMovementsTable sends: { type, params: { url, method, params, fileName } }
    const actionParams = meta.params || meta;
    const {
      params: metaParams,
      url: metaUrl,
      method: metaMethod,
      fileName: metaFileName,
    } = actionParams || {};
    // Use nested .params when present (cash export), otherwise use the whole payload (order export)
    let params =
      metaParams != null ? { ...metaParams } : { ...(actionParams || {}) };

    // const timeZone = getUserTimezone();
    // params.tz = timeZone;

    if (params.countryId) {
      params = { ...params, filterByCountry: params.countryId };
    }

    // Check if this is cash activities export
    // Check for both relative and absolute URLs (including localhost)
    const isCashActivitiesExportUrl =
      metaUrl &&
      (metaUrl.includes('cash/activities/export') ||
        metaUrl.includes('/cash/activities/export') ||
        (metaUrl.includes('localhost') &&
          metaUrl.includes('cash/activities/export')) ||
        metaUrl.includes('v1/cash/activities/export'));

    // If this is cash activities export, handle it separately and return early
    if (isCashActivitiesExportUrl) {
      // Transform parameters for cash activities export
      params = transformCashActivitiesParams(params);

      // For absolute URLs (like localhost), use axios directly without baseURL
      if (metaUrl.startsWith('http')) {
        const sessionToken = localStorage.getItem('access_token');
        const requestHeaders = {
          Accept: 'application/json',
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        };

        const axiosPayload = {
          url: metaUrl,
          method: metaMethod || 'get',
          params,
          headers: requestHeaders,
          responseType: 'blob',
        };

        const csvData = yield call(axios.request, axiosPayload);

        // DISCARD FILE DOWNLOAD IF STATUS IS 202
        if (csvData.status === 202) {
          yield put({
            type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
          });
          if (csvData.data instanceof Blob) {
            const dataJson = yield csvData.data.text();
            const dataParsed = JSON.parse(dataJson);
            toast.success(dataParsed.message);
          } else {
            toast.success(csvData.data.message);
          }
          return;
        }

        yield put({
          type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
        });

        const fileName =
          metaFileName ||
          `Analytics-Cash-Movements-${format(new Date(), 'yyyy-mm-dd hh:mm')}`;

        saveAs(csvData.data, `${fileName}.csv`);
        return;
      }

      // For relative URLs, use makeRequest with baseURL
      const payload = {
        url: metaUrl,
        method: metaMethod || 'get',
        params,
        responseType: 'blob',
      };
      const csvData = yield call(makeRequest, payload);

      // DISCARD FILE DOWNLOAD IF STATUS IS 202
      if (csvData.status === 202) {
        yield put({
          type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
        });
        if (csvData.data instanceof Blob) {
          const dataJson = yield csvData.data.text();
          const dataParsed = JSON.parse(dataJson);
          toast.success(dataParsed.message);
        } else {
          toast.success(csvData.data.message);
        }
        return;
      }

      yield put({
        type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
      });

      const fileName =
        metaFileName ||
        `Analytics-Cash-Movements-${format(new Date(), 'yyyy-mm-dd hh:mm')}`;

      if (csvData.data instanceof Blob) {
        saveAs(csvData.data, `${fileName}.csv`);
      } else {
        downloadCsvFile(csvData.data, fileName);
      }
      return;
    }

    // For non-cash activities export, use default order export URLs
    const url =
      metaUrl ||
      (params.breakdownType === ORDER_BREAKDOWN_TYPE.ITEM
        ? '/v2/analyseit/order/menus/export'
        : 'v2/analyseit/order/export');

    const payload = {
      url,
      method: metaMethod || 'get',
      params,
    };
    const csvData = yield call(makeRequest, payload);
    // DISCARD FILE DOWNLOAD IF STATUS IS 202
    if (csvData.status === 202) {
      yield put({
        type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
      });
      toast.success(csvData.data.message);
      return;
    }
    yield put({
      type: constants.LOAD_ANALYTICS_CSV_SUCCESS,
    });

    const fileName =
      metaFileName ||
      `Analytics-Orders-${format(new Date(), 'yyyy-mm-dd hh:mm')}`;

    downloadCsvFile(csvData.data, fileName);
  } catch (error) {
    yield put({ type: constants.LOAD_ANALYTICS_CSV_FAILED });
    const { message } = (error && error.response && error.response.data) || {};
    toast.error(message || printf(DOWNLOAD_FAILED_NO_INTERNET_MSG, 'File'));
  }
}

function* getStatus() {
  try {
    const payload = {
      url: 'v1/oni/statuses',
      method: 'get',
    };
    const statusData = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_STATUS_SUCCESS,
      analyticsOrderStatus: statusData.data,
    });
  } catch (error) {
    yield put({ type: constants.LOAD_STATUS_FAILED });
    toast.error(printf(LOAD_FAILED_NO_INTERNET_MSG, 'Status'));
  }
}
function* getItemBreakdown(meta) {
  try {
    const params = { ...meta.params };

    const { activeTab, ...rest } = params;
    // Menu reports are only reliable for a 7-day window; clamp so the BE doesn't
    // return silently-incomplete data for longer ranges.
    const { params: itemBreakDownParam } = clampPeriodToDays(rest);
    const urlType =
      ITEM_BREAKDOWN_TABS.find(tab => tab.id === activeTab) &&
      ITEM_BREAKDOWN_TABS.find(tab => tab.id === activeTab).key;

    const payload = {
      url: `v2/analyseit/order/${urlType}/analytics`,
      method: 'get',
      params: itemBreakDownParam,
    };
    const response = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_ITEM_BREAKDOWN_DATA_SUCCESSFUL,
      itemBreakDownData: response.data,
    });
  } catch (error) {
    yield put({ type: constants.LOAD_ITEM_BREAKDOWN_DATA_FAILED });
    toast.error(printf(LOAD_FAILED_NO_INTERNET_MSG, 'Items'));
  }
}
function* getBusyModeAnalyticsData(meta) {
  const { params } = meta || {};
  const selectedTimePeriod = yield select(makeSelectSelectedTimePeriod());
  const selectedProviders = yield select(makeSelectAnalyticsSelectedProvider());

  try {
    const payload = {
      url: `v2/analyseit/busymode/analytics`,
      method: 'GET',
      params,
    };
    const response = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_BUSY_MODE_ANALYTICS_DATA_SUCCESSFUL,
      busyModeAnalyticsData: response.data,
    });
    yield put({
      type: constants.CHANGE_SELECTED_PROVIDERS_LIST_FOR_PROVIDER_COMPARISON,
      selectedProviderListForProviderComparison: cloneDeep(selectedProviders),
    });
    yield put({
      type: constants.CHANGE_TIME_PERIOD_SLUG_FOR_DATA_CALCULATION,
      timePeriodSlug: selectedTimePeriod && selectedTimePeriod.slug,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_BUSY_MODE_ANALYTICS_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {
      message: printf(LOAD_FAILED_NO_INTERNET_MSG, 'analytics'),
    };
    toast.error(message);
  }
}

function* getBusyModeLogData(meta) {
  const { params } = meta || {};

  try {
    const payload = {
      url: `v2/analyseit/busymode/logs`,
      method: 'GET',
      params,
    };
    const response = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_BUSY_MODE_LOG_DATA_SUCCESSFUL,
      busyModeLogData: response.data,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_BUSY_MODE_LOG_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {
      message: printf(LOAD_FAILED_NO_INTERNET_MSG, 'analytics'),
    };
    toast.error(message);
  }
}

function* getPaymentMethodAnalyticsData(meta) {
  const { params } = meta || {};

  try {
    const payload = {
      url: `v2/analyseit/order/payment/summary`,
      method: 'GET',
      params,
    };
    const response = yield call(makeRequest, payload);
    yield put({
      type: constants.LOAD_PAYMENT_METHOD_DATA_SUCCESSFUL,
      paymentMethodAnalytics: response.data,
    });
  } catch (err) {
    yield put({ type: constants.LOAD_PAYMENT_METHOD_DATA_FAILED });
    const { message } = (err && err.response && err.response.data) || {
      message: printf(LOAD_FAILED_NO_INTERNET_MSG, 'analytics'),
    };
    toast.error(message);
  }
}

function* getList(meta) {
  const { params: metaParams } = meta || {};
  const { url, params, reducerKey } = metaParams || {};

  try {
    const payload = {
      url,
      method: 'GET',
      params,
    };
    const resp = yield call(makeRequest, payload);

    const list =
      resp?.data?.accounts || resp?.data?.results || resp?.data || [];

    yield put({
      type: constants.GET_LIST_SUCCESS,
      payload: {
        key: reducerKey,
        value: list,
      },
    });
  } catch (err) {
    yield put({
      type: constants.GET_LIST_FAILED,
      payload: {
        key: reducerKey,
      },
    });
    showErrMsg(err);
  }
}

function* getData(req) {
  const { reducerKey, url, method, params, callbackFn } = req.meta || {};

  try {
    const payload = {
      url,
      method,
      params,
    };

    const res = yield call(makeRequest, payload);
    const details = res.data.results || res.data || [];
    const successReducerPayload = {};

    if (!isEmpty(reducerKey)) {
      successReducerPayload[reducerKey] = details;
    }

    yield put({
      type: constants.GET_DATA_SUCCESS,
    });

    yield put({
      type: constants.EDIT_FIELD,
      params: successReducerPayload,
    });

    if (isFunction(callbackFn)) {
      callbackFn(details, res);
    }
  } catch (err) {
    yield put({
      type: constants.GET_DATA_FAILED,
    });

    if (!isEmpty(reducerKey)) {
      yield put({
        type: constants.EDIT_FIELD,
        params: {
          [reducerKey]: null,
        },
      });
    }

    const { message } = err?.response?.data || {};
    toast.error(message || `Failed to load ${reducerKey}`);
  }
}

function* getCSV(meta) {
  try {
    const { url, method, params: parameters, fileName } = meta.payload || {};

    // const timeZone = getUserTimezone();
    // params.tz = timeZone;

    const params = { ...parameters };

    if (params.countryId) {
      params.filterByCountry = parameters.countryId;
    }

    const payload = {
      url,
      method,
      params,
    };

    const response = yield call(makeRequest, payload);
    const { status, data } = response || {};

    yield put({
      type: constants.LOAD_CSV_SUCCESS,
    });

    // DISCARD FILE DOWNLOAD IF STATUS IS 202
    if (status === 202) {
      toast.success(data.message);
      return;
    }

    downloadCsvFile(
      data,
      `${fileName}-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  } catch (error) {
    yield put({ type: constants.LOAD_CSV_FAILED });
    toast.error(printf(DOWNLOAD_FAILED_NO_INTERNET_MSG, 'File'));
  }
}

function* getXLSX(meta) {
  try {
    // Handle both direct payload and nested payload structure
    const metaPayload = meta.payload || meta;
    const { url, method, params: parameters, fileName } = metaPayload || {};

    if (!url || !method) {
      toast.error('Missing URL or method for export');
      return;
    }

    let params = { ...parameters };

    if (params.countryId) {
      params.filterByCountry = parameters.countryId;
    }

    // Transform parameters for cash activities export
    // Check for both relative and absolute URLs (including localhost)
    const isCashActivitiesExport =
      url &&
      (url.includes('cash/activities/export') ||
        url.includes('/cash/activities/export') ||
        (url.includes('localhost') && url.includes('cash/activities/export')));

    if (isCashActivitiesExport) {
      params = transformCashActivitiesParams(params);
    }

    // For absolute URLs (like localhost), use axios directly without baseURL
    // For relative URLs, use makeRequest which uses baseURL from config
    if (isCashActivitiesExport && url.startsWith('http')) {
      const sessionToken = localStorage.getItem('access_token');
      const requestHeaders = {
        Accept: 'application/json',
        ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
      };

      const axiosPayload = {
        url,
        method,
        params,
        headers: requestHeaders,
        responseType: 'blob',
      };

      const response = yield call(axios.request, axiosPayload);
      const { status, data } = response || {};

      yield put({
        type: constants.LOAD_XLSX_SUCCESS,
      });

      // DISCARD FILE DOWNLOAD IF STATUS IS 202
      if (status === 202) {
        if (data instanceof Blob) {
          const dataJson = yield data.text();
          const dataParsed = JSON.parse(dataJson);
          toast.success(dataParsed.message);
        } else {
          toast.success(data.message);
        }
        return;
      }

      downloadXLSXFile(
        data,
        `${fileName}-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
      );
      return;
    }

    const payload = {
      url,
      method,
      params,
      responseType: 'blob',
    };

    const response = yield call(makeRequest, payload);
    const { status, data } = response || {};

    yield put({
      type: constants.LOAD_XLSX_SUCCESS,
    });

    // DISCARD FILE DOWNLOAD IF STATUS IS 202
    if (status === 202) {
      if (data instanceof Blob) {
        const dataJson = yield data.text();
        const dataParsed = JSON.parse(dataJson);
        toast.success(dataParsed.message);
      } else {
        toast.success(data.message);
      }
      return;
    }

    downloadXLSXFile(
      data,
      `${fileName}-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  } catch (error) {
    yield put({ type: constants.LOAD_XLSX_FAILED });
    const { message } = (error && error.response && error.response.data) || {};
    toast.error(message || printf(DOWNLOAD_FAILED_NO_INTERNET_MSG, 'File'));
  }
}

export function* loadList(req) {
  const {
    reducerKey,
    loaderKey,
    url,
    method,
    params,
    isPaginated = true,
    callbackFn,
    errorCallbackFn,
  } = req.meta || {};

  try {
    const payload = {
      url,
      method,
      params,
    };
    const res = yield call(makeRequest, payload);
    const list = res.data.results || res.data || [];

    yield put({
      type: constants.ACTION_RESOLVED,
      meta: { loaderKey },
    });

    if (!isEmpty(reducerKey)) {
      const successReducerPayload = {
        [reducerKey]: list,
      };

      if (isPaginated) {
        successReducerPayload.total = res.data.total;
        successReducerPayload.page = res.data.page;
        successReducerPayload.size = res.data.size;
      }

      yield put({
        type: constants.UPDATE_ENTITY,
        meta: successReducerPayload,
      });
    }

    if (isFunction(callbackFn)) {
      callbackFn(list, res);
    }
  } catch (err) {
    yield put({
      type: constants.ACTION_RESOLVED,
      meta: { loaderKey },
    });

    if (!isEmpty(reducerKey)) {
      yield put({
        type: constants.UPDATE_ENTITY,
        meta: {
          [reducerKey]: [],
        },
      });
    }

    if (isFunction(errorCallbackFn)) {
      errorCallbackFn(err);
    }

    const { message } = err?.response?.data || {};
    toast.error(message || `Failed to load ${reducerKey}`);
  }
}

function* getEntity(req) {
  const {
    reducerKey,
    loaderKey,
    url,
    method,
    params,
    callbackFn,
    errorCallbackFn,
  } = req.meta || {};

  try {
    const payload = {
      url,
      method,
      params,
    };
    const res = yield call(makeRequest, payload);
    const data = res.data.results || res.data || {};

    yield put({
      type: constants.ACTION_RESOLVED,
      meta: { loaderKey },
    });

    if (!isEmpty(reducerKey)) {
      const successReducerPayload = {
        [reducerKey]: data,
      };

      yield put({
        type: constants.UPDATE_ENTITY,
        meta: successReducerPayload,
      });
    }

    if (isFunction(callbackFn)) {
      callbackFn(data, res);
    }
  } catch (err) {
    yield put({
      type: constants.ACTION_RESOLVED,
      meta: { loaderKey },
    });

    if (!isEmpty(reducerKey)) {
      yield put({
        type: constants.UPDATE_ENTITY,
        meta: {
          [reducerKey]: [],
        },
      });
    }

    if (isFunction(errorCallbackFn)) {
      errorCallbackFn(err);
    }

    const { message } = err?.response?.data || {};
    toast.error(message || `Failed to load ${reducerKey}`);
  }
}

export default function* nativeAnalyticsSaga() {
  yield takeLatest(constants.LOAD_SUMMARY, getSummary);
  yield takeLatest(constants.LOAD_ANALYTICS_DATA, getAnalyticsData);
  yield takeLatest(constants.LOAD_ORDERS_DATA, getOrdersData);
  yield takeLatest(constants.LOAD_MIRROR_ORDERS_DATA, getMirrorOrdersData);
  yield takeLatest(constants.LOAD_CASH_MOVEMENTS_DATA, getCashMovementsData);
  yield takeLatest(constants.LOAD_CASH_SHIFTS_DATA, getCashShiftsData);
  yield takeLatest(constants.UPDATE_CASH_ACTIVITY, updateCashActivitySaga);
  yield takeLatest(constants.DELETE_CASH_ACTIVITY, deleteCashActivitySaga);
  yield takeLatest(
    constants.LOAD_CASH_SHIFT_ACTIVITIES,
    getCashShiftActivities,
  );
  yield takeLatest(constants.LOAD_ANALYTICS_BUSINESS, getBusinesses);
  yield takeLatest(constants.LOAD_ANALYTICS_BRANDS, getBrands);
  yield takeLatest(constants.LOAD_ANALYTICS_BRANCHES, getBranches);
  yield takeLatest(constants.LOAD_ANALYTICS_PROVIDERS, getProviders);
  yield takeLatest(constants.LOAD_ANALYTICS_CSV, getAnalyticsCsv);
  yield takeLatest(constants.LOAD_STATUS, getStatus);
  yield takeLatest(constants.LOAD_ITEM_BREAKDOWN_DATA, getItemBreakdown);
  yield takeLatest(
    constants.LOAD_BUSY_MODE_ANALYTICS_DATA,
    getBusyModeAnalyticsData,
  );
  yield takeLatest(constants.LOAD_BUSY_MODE_LOG_DATA, getBusyModeLogData);
  yield takeLatest(
    constants.LOAD_PAYMENT_METHOD_DATA,
    getPaymentMethodAnalyticsData,
  );

  yield takeLatest(constants.LOAD_CSV, getCSV);
  yield takeLatest(constants.LOAD_XLSX, getXLSX);
  yield takeEvery(constants.LOAD_LIST, loadList);

  yield takeLatest(constants.GET_DATA, getData);
  yield takeEvery(constants.GET_LIST, getList);
  yield takeEvery(constants.GET_ENTITY, getEntity);
}
